import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './CategoryModal.css';

function CategoryModal({ category, onClose }) {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState('value'); // 'value', 'price'

  // Disable body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Get coins from category (supports both _childCoins and children)
  const coins = category?._childCoins || category?.children || [];

  // Sort coins based on selected criteria
  const sortedCoins = useMemo(() => {
    if (!coins || coins.length === 0) return [];

    return [...coins].sort((a, b) => {
      if (sortBy === 'value') {
        // Sort by volume/value (market cap proxy)
        return (b.volume || 0) - (a.volume || 0);
      } else if (sortBy === 'price') {
        // Sort by 24h price change
        return (b.change || 0) - (a.change || 0);
      }
      return 0;
    });
  }, [coins, sortBy]);

  // Calculate average change for the category
  const avgChange = useMemo(() => {
    if (!coins || coins.length === 0) return 0;
    const total = coins.reduce((sum, coin) => sum + (coin.change || 0), 0);
    return total / coins.length;
  }, [coins]);

  // Format price
  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    if (price >= 1000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    if (price >= 1) return `$${price.toFixed(2)}`;
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    if (price >= 0.0001) return `$${price.toFixed(6)}`;
    return `$${price.toFixed(8)}`;
  };

  // Format volume
  const formatVolume = (volume) => {
    if (!volume && volume !== 0) return '-';
    if (volume >= 1e12) return `${(volume / 1e12).toFixed(1)}T`;
    if (volume >= 1e9) return `${(volume / 1e9).toFixed(1)}B`;
    if (volume >= 1e6) return `${(volume / 1e6).toFixed(0)}M`;
    if (volume >= 1e3) return `${(volume / 1e3).toFixed(0)}K`;
    return volume.toFixed(0);
  };

  if (!category) return null;

  return (
    <div className="category-modal-overlay" onClick={onClose}>
      <div className="category-modal-content" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-row">
            <div className="modal-header-left">
              <div className="modal-category-icon">
                {category.name?.substring(0, 2) || '#'}
              </div>
              <h2 className="modal-title">{category.name}</h2>
            </div>

            {/* Sort Controls - same row as title */}
            <div className="modal-sort-controls">
              <span className="sort-label">SORT:</span>
              <div className="sort-buttons">
                <button
                  className={sortBy === 'value' ? 'active' : ''}
                  onClick={() => setSortBy('value')}
                >
                  VOLUME $
                </button>
                <button
                  className={sortBy === 'price' ? 'active' : ''}
                  onClick={() => setSortBy('price')}
                >
                  PRICE 24h
                </button>
              </div>
            </div>
          </div>

          <button className="modal-close-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Table Header */}
        <div className="modal-table-header">
          <div className="col-asset">Asset</div>
          <div className="col-price">Price (24h)</div>
          <div className="col-volume">Volume $</div>
        </div>

        {/* Coins List */}
        <div className="modal-coins-list">
          {sortedCoins.map((coin, index) => {
            const isPositive = (coin.change || 0) >= 0;

            return (
              <div key={coin.symbol || index} className="coin-row">
                <div className="col-asset">
                  <div className={`coin-icon ${isPositive ? 'positive' : 'negative'}`}>
                    {(coin.symbol || coin.name || '?').charAt(0)}
                  </div>
                  <div className="coin-info">
                    <span className="coin-symbol">{coin.symbol || coin.name}</span>
                    <span className="coin-price">{formatPrice(coin.price)}</span>
                  </div>
                </div>

                <div className="col-price">
                  <span className={`price-change ${isPositive ? 'positive' : 'negative'}`}>
                    {isPositive ? '▲' : '▼'} {Math.abs(coin.change || 0).toFixed(2)}%
                  </span>
                </div>

                <div className="col-volume">
                  <span className="volume-value">{formatVolume(coin.volume)}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="avg-performance">
            <span className="avg-label">AVG PERF:</span>
            <span className={`avg-value ${avgChange >= 0 ? 'positive' : 'negative'}`}>
              {avgChange >= 0 ? '+' : ''}{avgChange.toFixed(2)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CategoryModal;
